import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Avatar,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller, Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { setProfileData } from "../../store/slices/profileSlice";
import {
  useUpdateProfile,
  useUploadProfileImage
} from "../../hooks/api/useProfile";
import {
  useKivunovaTranslation,
  PhoneCountryCodeMap,
  splitPhoneNumber,
  combinePhoneNumber,
  isValidPhoneNumberForCountry,
  BRAND_COLOR,
  BRAND_HOVER,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  useResponsive
} from "@kivunova/kivufrontendcommon";
import {
  UpdateProfileFormData,
  ProfileEditFormData,
  ProfileEditFormProps
} from "./types/profileTypes";
import { CountryCode } from "libphonenumber-js";

const ProfileEditForm: React.FC<ProfileEditFormProps> = ({
  onClose,
  setAlert
}) => {
  const { t } = useKivunovaTranslation();
  const dispatch = useDispatch();
  const { profileData } = useSelector((state: RootState) => state.profile);
  const updateProfile = useUpdateProfile();
  const uploadProfileImage = useUploadProfileImage();

  // ── Responsive ─────────────────────────────────────────────────────────────
  const { inputSize } = useResponsive();

  const [previewImage, setPreviewImage] = useState<string>(
    profileData?.profileImage || ""
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const schema = Yup.object({
    firstName: Yup.string().required(
      t("first-name-required", "First name is required")
    ),
    lastName: Yup.string().required(
      t("last-name-required", "Last name is required")
    ),
    phoneNumber: Yup.string()
      .required(t("phone-required", "Phone number is required"))
      .test(
        "valid-phone",
        t("invalid-phone", "Invalid phone number for selected country"),
        function (value) {
          const { phoneCountryCode } = this.parent;
          if (!value || !phoneCountryCode) return false;
          const countryCodeValue =
            PhoneCountryCodeMap[phoneCountryCode]?.code || phoneCountryCode;
          const countryKey = Object.keys(PhoneCountryCodeMap).find(
            (key) => PhoneCountryCodeMap[key].code === countryCodeValue
          );
          if (!countryKey) return false;
          const countryCode = countryKey as CountryCode;
          return isValidPhoneNumberForCountry(
            value,
            countryCode,
            countryCodeValue
          );
        }
      ),
    phoneCountryCode: Yup.string().required(
      t("country-code-required", "Country code is required")
    ),
    profileImage: Yup.string().optional()
  });

  const initialPhoneSplit = splitPhoneNumber(
    profileData?.phone || "",
    PhoneCountryCodeMap
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ProfileEditFormData>({
    resolver: yupResolver(schema) as Resolver<ProfileEditFormData>,
    defaultValues: {
      firstName: profileData?.firstName || "",
      lastName: profileData?.lastName || "",
      phoneNumber: initialPhoneSplit.number,
      phoneCountryCode: initialPhoneSplit.code,
      profileImage: profileData?.profileImage || ""
    }
  });

  useEffect(() => {
    if (profileData) {
      const phoneSplit = splitPhoneNumber(
        profileData.phone,
        PhoneCountryCodeMap
      );
      reset({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phoneNumber: phoneSplit.number,
        phoneCountryCode: phoneSplit.code,
        profileImage: profileData.profileImage || ""
      });
      setPreviewImage(profileData.profileImage || "");
    }
  }, [profileData, reset]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const getInitials = () => {
    const first = profileData?.firstName?.[0] || "";
    const last = profileData?.lastName?.[0] || "";
    return `${first}${last}`.toUpperCase();
  };

  const onSubmit = async (data: ProfileEditFormData) => {
    if (updateProfile.isPending || uploadProfileImage.isPending) return;

    try {
      let imageUrl: string | undefined = data.profileImage || undefined;

      if (selectedFile) {
        try {
          const uploadResult =
            await uploadProfileImage.mutateAsync(selectedFile);
          if (uploadResult?.uploadProfileImage?.code === API_RESPONSE_CODE.OK) {
            imageUrl =
              uploadResult.uploadProfileImage.user.profileImage || undefined;
          } else {
            setAlert({
              severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
              code: ALERT_BANNER_CODE.IMAGE_UPLOAD_FAILED
            });
            return;
          }
        } catch (uploadError: unknown) {
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code:
              uploadError instanceof Error
                ? (uploadError.message as ALERT_BANNER_CODE)
                : ALERT_BANNER_CODE.IMAGE_UPLOAD_FAILED
          });
          return;
        }
      }

      const fullPhoneNumber = combinePhoneNumber(
        data.phoneCountryCode,
        data.phoneNumber,
        PhoneCountryCodeMap
      );

      const updatePayload: UpdateProfileFormData = {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: fullPhoneNumber,
        ...(imageUrl !== undefined && { profileImage: imageUrl })
      };

      const result = await updateProfile.mutateAsync(updatePayload);
      const { code } = result.updateProfile;

      if (code === API_RESPONSE_CODE.OK) {
        const updatedUser = result.updateProfile.user;
        dispatch(
          setProfileData({
            ...profileData!,
            firstName: updatedUser.firstName,
            lastName: updatedUser.lastName,
            phone: updatedUser.phone,
            profileImage:
              updatedUser.profileImage || profileData?.profileImage || ""
          })
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.PROFILE_UPDATED
        });
        onClose();
      } else {
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: code as ALERT_BANNER_CODE
        });
      }
    } catch (error: unknown) {
      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code:
          error instanceof Error
            ? (error.message as ALERT_BANNER_CODE)
            : ALERT_BANNER_CODE.INTERNAL_SERVER_ERROR
      });
    }
  };

  const isUploading = uploadProfileImage.isPending;
  const isSaving = updateProfile.isPending;
  const isLoading = isUploading || isSaving;

  return (
    // Here we can use CSS modal structure: .modal-styles > .modal-header + .modal-body + .modal-footer
    <Box className="modal-styles">
      {/* ── Modal Header ──────────────────────────────────────────────────── */}
      <Box
        className="modal-header"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        <Typography variant="h6" fontWeight={600} color="text.primary">
          {t("edit-profile", "Edit your profile")}
        </Typography>
        <IconButton
          onClick={onClose}
          disabled={isLoading}
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Modal Body (scrollable) ────────────────────────────────────────── */}
      <Box className="modal-body">
        {/* Profile Image Upload */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 3,
            position: "relative"
          }}
        >
          <Box sx={{ position: "relative" }}>
            <Avatar
              src={previewImage}
              alt={profileData?.firstName}
              sx={{
                width: 110,
                height: 110,
                fontSize: "2.25rem",
                bgcolor: BRAND_COLOR,
                border: `4px solid ${BRAND_COLOR}20`,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
              }}
            >
              {!previewImage && getInitials()}
            </Avatar>

            {isUploading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(0,0,0,0.5)",
                  borderRadius: "50%"
                }}
              >
                <CircularProgress size={36} sx={{ color: "white" }} />
              </Box>
            )}

            <input
              accept="image/*"
              style={{ display: "none" }}
              id="profile-image-upload"
              type="file"
              onChange={handleImageChange}
              disabled={isLoading}
            />
            <label htmlFor="profile-image-upload">
              <IconButton
                component="span"
                disabled={isLoading}
                className="touch-target"
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  bgcolor: BRAND_COLOR,
                  color: "white",
                  width: 36,
                  height: 36,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  "&:hover": { bgcolor: BRAND_HOVER, transform: "scale(1.05)" },
                  transition: "all 0.2s ease-in-out"
                }}
              >
                <CameraAltIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </label>
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1.5, textAlign: "center" }}
          >
            {t(
              "click-camera-to-upload",
              "Click the camera icon to upload a new photo"
            )}
          </Typography>
        </Box>

        {/* Form Fields: .form-stack for vertical layout */}
        <Box
          component="form"
          id="profile-edit-form"
          onSubmit={handleSubmit(onSubmit)}
          className="form-stack"
          noValidate
        >
          {/* First Name */}
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("first-name", "First Name")}
                fullWidth
                size={inputSize}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                disabled={isLoading}
              />
            )}
          />

          {/* Last Name */}
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("last-name", "Last Name")}
                fullWidth
                size={inputSize}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                disabled={isLoading}
              />
            )}
          />

          {/* Phone: .phone-field-row: code picker (fixed) + number (flex-1) */}
          <Box className="phone-field-row">
            <FormControl
              className="phone-field-row__code"
              variant="outlined"
              size={inputSize}
              error={!!errors.phoneCountryCode}
              disabled={isLoading}
            >
              <InputLabel id="phone-country-code-label">
                {t("country-code", "Code")}
              </InputLabel>
              <Controller
                name="phoneCountryCode"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? "+250"}
                    onChange={(e) => field.onChange(e.target.value)}
                    labelId="phone-country-code-label"
                    id="phone-country-code"
                    label={t("country-code", "Code")}
                  >
                    {Object.keys(PhoneCountryCodeMap).map((key) => (
                      <MenuItem key={key} value={key}>
                        {`${PhoneCountryCodeMap[key].flag} ${PhoneCountryCodeMap[key].code}`}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.phoneCountryCode && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.75 }}
                >
                  {errors.phoneCountryCode.message}
                </Typography>
              )}
            </FormControl>

            <Box className="phone-field-row__number">
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("phone-number", "Phone Number")}
                    fullWidth
                    size={inputSize}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                    placeholder="782484464"
                    disabled={isLoading}
                  />
                )}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Modal Footer — Save (primary) + Cancel, CSS handles mobile stacking */}
      <Box className="modal-footer">
        {/* Cancel rendered first in DOM; CSS reverses on desktop via row, stacks on mobile */}
        <Button
          type="button"
          variant="outlined"
          onClick={onClose}
          disabled={isLoading}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            color: BRAND_COLOR,
            borderColor: BRAND_COLOR,
            "&:hover": {
              backgroundColor: BRAND_HOVER,
              color: "white",
              borderColor: BRAND_HOVER
            }
          }}
        >
          {t("cancel", "Cancel")}
        </Button>

        <LoadingButton
          type="submit"
          form="profile-edit-form"
          variant="contained"
          loading={isLoading}
          className="brand-button"
          sx={{
            textTransform: "none",
            borderRadius: 2
          }}
        >
          {t("save-changes", "Save Changes")}
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default ProfileEditForm;
